import React, { lazy, useMemo } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useFormikContext, getIn } from 'formik';

import { brandConstants, brandConfig } from 'brand-config';
import Field from 'core/components/form/form.v2';
import Divider from 'core/components/divider';
import Label from 'core/components/label/label';

import useStyles from './exclusions.styles';

const BaseCosmeticDamageExclusionSection = ({ fieldName, disabled, classes }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const cosmeticDamageExclusion = getIn(values, `${fieldName}.cosmeticDamageExclusion`);
  const exteriorDoorsWindowsDamageExclusion = getIn(values, `${fieldName}.exteriorDoorsWindowsDamageExclusion`);
  const exteriorWallsDamageExclusion = getIn(values, `${fieldName}.exteriorWallsDamageExclusion`);
  const roofsDamageExclusion = getIn(values, `${fieldName}.roofsDamageExclusion`);

  const updateCosmeticDamageExclusion = (newExteriorDoors, newExteriorWalls, newRoofs) => {
    // Use the new values to determine if main exclusion should be enabled
    const hasAnySelection = newExteriorDoors || newExteriorWalls || newRoofs;

    setFieldValue(`${fieldName}.cosmeticDamageExclusion`, hasAnySelection);
    setFieldTouched(`${fieldName}.cosmeticDamageExclusion`, true);
  };

  const handleMainSwitchChange = (value) => {
    setFieldValue(`${fieldName}.cosmeticDamageExclusion`, value);
    setFieldTouched(`${fieldName}.cosmeticDamageExclusion`, true);

    // If turning off main exclusion, clear all individual selections
    if (!value) {
      setFieldValue(`${fieldName}.exteriorDoorsWindowsDamageExclusion`, false);
      setFieldValue(`${fieldName}.exteriorWallsDamageExclusion`, false);
      setFieldValue(`${fieldName}.roofsDamageExclusion`, false);
    }
  };

  return (
    <Grid item xs={12}>
      <div className={classes.container}>
        <Grid container>
          <Field
            xs={12}
            type="switch"
            id={`${fieldName}.cosmeticDamageExclusion`}
            name={`${fieldName}.cosmeticDamageExclusion`}
            mode="dark"
            label="Cosmetic damage exclusion"
            disabled={disabled}
            onChange={(value) => handleMainSwitchChange(value)}
          />
        </Grid>
        {cosmeticDamageExclusion && (
          <>
            <Divider className={classes.divider} />
            <Label className={classes.label}>apply cosmetic damage exclusion to these surfacing types:</Label>
            <Grid container spacing={2}>
              <Field
                xs={5}
                type="checkbox"
                id={`${fieldName}.exteriorDoorsWindowsDamageExclusion`}
                name={`${fieldName}.exteriorDoorsWindowsDamageExclusion`}
                mode="dark"
                label="Exterior doors & windows"
                disabled={disabled}
                onChange={(value) => {
                  updateCosmeticDamageExclusion(value, exteriorWallsDamageExclusion, roofsDamageExclusion);
                }}
              />
              <Field
                xs={4}
                type="checkbox"
                id={`${fieldName}.exteriorWallsDamageExclusion`}
                name={`${fieldName}.exteriorWallsDamageExclusion`}
                mode="dark"
                label="Exterior walls"
                disabled={disabled}
                onChange={(value) => {
                  updateCosmeticDamageExclusion(exteriorDoorsWindowsDamageExclusion, value, roofsDamageExclusion);
                }}
              />
              <Field
                xs={3}
                type="checkbox"
                id={`${fieldName}.roofsDamageExclusion`}
                name={`${fieldName}.roofsDamageExclusion`}
                mode="dark"
                label="Roofs"
                disabled={disabled}
                onChange={(value) => {
                  updateCosmeticDamageExclusion(
                    exteriorDoorsWindowsDamageExclusion,
                    exteriorWallsDamageExclusion,
                    value
                  );
                }}
              />
            </Grid>
          </>
        )}
      </div>
    </Grid>
  );
};

const CosmeticDamageExclusionSection = ({ fieldName, disabled, state, riskType, ...rest }) => {
  const classes = useStyles();
  const BrandDamageExclusionSectionComponent = useMemo(() => {
    const customComponentPath =
      brandConfig.farm[state]?.[riskType]?.customFormComponentPaths?.cosmeticDamageExclusionSection;

    // If a custom component path is provided, we import the component from the brand-components folder dynamically
    if (customComponentPath) {
      const [risk, path] = customComponentPath.split('/');
      return lazy(
        () =>
          import(
            `./../../../../../../common/components/brand-components/${brandConstants.SHORT_NAME.toLowerCase()}/farm/${risk}/${state}/${path}.jsx`
          )
      );
    }

    return null;
  }, [riskType, state]);

  if (BrandDamageExclusionSectionComponent) {
    return (
      <BrandDamageExclusionSectionComponent fieldName={fieldName} disabled={disabled} classes={classes} {...rest} />
    );
  }
  return <BaseCosmeticDamageExclusionSection fieldName={fieldName} disabled={disabled} classes={classes} {...rest} />;
};

CosmeticDamageExclusionSection.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

CosmeticDamageExclusionSection.defaultProps = {
  disabled: false
};

export default observer(CosmeticDamageExclusionSection);
